interface AnimationState {
  frameRange?: {
    start: number;
    end: number;
  };
  customTrigger?: string;
  altText: string;
  state_dependencies?: {
    views?: string[];
    hotspots?: Array<{ id: string }>;
    customTriggers?: string[];
  };
}

interface Entity {
  type: string;
  id?: string;
  states?: AnimationState[];
  x_offset?: number;
  y_offset?: number;
  width?: number;
  height?: number;
}

interface ActiveAnimation {
  entityId: string;
  entityIndex: number;
  currentStateIndex: number;
  totalStates: number;
  stateData: AnimationState;
  gridPosition: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
}

export const generateAnimationStateColors = (
  entities: Entity[]
): Map<string, string> => {
  const colorMap = new Map<string, string>();
  const animations = entities.filter((e) => e.type === "animation");

  const goldenRatioConjugate = 0.618033988749895;
  let hue = Math.random();

  animations.forEach((anim, index) => {
    hue += goldenRatioConjugate;
    hue %= 1;
    const baseHue = Math.floor(hue * 360);

    anim.states?.forEach((state, stateIndex) => {
      const animId = anim.id || `anim-${index}`;
      const key = `${animId}-${stateIndex}`;

      const lightness = 45 + ((stateIndex * 15) % 35);
      const saturation = 65 + ((stateIndex * 20) % 30);

      colorMap.set(key, `hsl(${baseHue}, ${saturation}%, ${lightness}%)`);
    });
  });

  return colorMap;
};

export const getActiveAnimations = (
  entities: Entity[],
  checkEntityState: (entity: Entity) => number | null
): ActiveAnimation[] => {
  return entities
    .filter((e) => e.type === "animation")
    .map((entity, index) => {
      const state = checkEntityState(entity);
      if (state === null) return null;

      const animation: ActiveAnimation = {
        entityId: entity.id || `anim-${index}`,
        entityIndex: index,
        currentStateIndex: state,
        totalStates: entity.states?.length || 0,
        stateData: entity.states![state],
        gridPosition: {
          ...(entity.x_offset !== undefined && { x: entity.x_offset }),
          ...(entity.y_offset !== undefined && { y: entity.y_offset }),
          ...(entity.width !== undefined && { width: entity.width }),
          ...(entity.height !== undefined && { height: entity.height }),
        },
      };
      return animation;
    })
    .filter((anim): anim is ActiveAnimation => anim !== null);
};
